import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ROLE_LABEL } from "../lib/auth";

export default function Users() {
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ email: "", password: "", name: "", role: "billing", branch_id: "" });

  const load = () => api.get("/users").then(r => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/branches").then(r => {
      setBranches(r.data);
      if (r.data.length > 0) setF(p => ({ ...p, branch_id: p.branch_id || r.data[0].id }));
    });
  }, []);

  const save = async () => {
    if (!f.email || !f.password || !f.name) { toast.error("All fields required"); return; }
    try {
      await api.post("/users", f);
      toast.success("User created");
      setOpen(false);
      setF({ email: "", password: "", name: "", role: "billing", branch_id: branches[0]?.id || "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => { try { await api.delete(`/users/${id}`); load(); } catch { toast.error("Failed"); } };

  const branchName = (id) => branches.find(b => b.id === id)?.name || "—";

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Access</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Staff & Roles</h1>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="add-user-btn"><Plus className="w-4 h-4 mr-1" />New staff</Button>
      </div>

      <Card className="p-4">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Branch</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map(u => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-sm">{u.email}</TableCell>
                <TableCell><Badge variant="outline">{ROLE_LABEL[u.role] || u.role}</Badge></TableCell>
                <TableCell className="text-sm">{branchName(u.branch_id)}</TableCell>
                <TableCell>{u.is_active ? <Badge variant="secondary">active</Badge> : <Badge variant="destructive">inactive</Badge>}</TableCell>
                <TableCell className="text-right">
                  {u.is_active && <Button variant="ghost" size="sm" onClick={() => del(u.id)} data-testid={`del-user-${u.id}`}><Trash2 className="w-4 h-4" /></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New staff</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div><Label>Name</Label><Input value={f.name} onChange={e => setF(p => ({ ...p, name: e.target.value }))} data-testid="user-name" /></div>
            <div><Label>Email</Label><Input value={f.email} onChange={e => setF(p => ({ ...p, email: e.target.value }))} data-testid="user-email" /></div>
            <div><Label>Password</Label><Input type="password" value={f.password} onChange={e => setF(p => ({ ...p, password: e.target.value }))} data-testid="user-pass" /></div>
            <div><Label>Role</Label>
              <Select value={f.role} onValueChange={v => setF(p => ({ ...p, role: v }))}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="billing">Billing</SelectItem>
                  <SelectItem value="grooming">Grooming</SelectItem>
                  <SelectItem value="inventory">Inventory</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Branch</Label>
              <Select value={f.branch_id} onValueChange={v => setF(p => ({ ...p, branch_id: v }))}>
                <SelectTrigger data-testid="user-branch"><SelectValue placeholder="Pick branch" /></SelectTrigger>
                <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-user">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
